import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import {
    SPLASH_HANDED_OVER,
    hasSeen,
    rememberSeen,
    sessionMemory,
} from '../../application/onboarding/seenOnce';
import { BrandBadge, BrandWordmark } from '../chrome/BrandMark';

/**
 * The brand arriving (FR-01).
 *
 * Not a marketing page and not a loading screen with a logo on it: a staged
 * sequence on a tarmac-black field that ends by handing the reader to the
 * garage. Five stages, in the order §03 draws the lockup being built —
 *
 *   1. speed streaks tear in from the left on the heat ramp
 *   2. the chrome LEGENDS wordmark arrives with them, overshoots, settles
 *   3. the L badge drops in behind it and the streaks fade back
 *   4. the proposition and the tagline fade in under the lockup
 *   5. a heat-ramp track fills, and the screen hands over
 *
 * ALL OF IT IS CSS, driven off one class that this component adds on mount, and
 * that is the decision the whole file hangs on. A sequence stepped through in
 * React is a sequence that can desync: a slow first paint, a re-render from a
 * context above, a tab backgrounded halfway through, and stage three is playing
 * over stage one. One class, one set of `animation-delay`s off one schedule, and
 * the browser keeps them in step whatever this component does.
 *
 * The SCHEDULE lives here rather than in the sheet, because JavaScript needs it
 * too — the handover has to happen when the fill completes, and the fill's
 * duration is the same number the sheet animates. Two copies of that number
 * drift, so there is one, and it reaches CSS as custom properties on the root
 * element. This is the same argument `tokens.css` makes for `--duration-deal`,
 * applied to the timings the splash owns.
 *
 * It hands over BOTH ways. Automatically when the fill completes, and on any tap
 * or key press before that — nobody should have to sit through a brand moment
 * twice, and the second time is exactly when they will reach for the screen.
 * Once it has handed over in a session it does not play again at all: coming
 * back to `/en` thirty seconds later gets the still lockup and an immediate
 * redirect, so the front door never becomes a wall.
 *
 * Navigation is `replace`, so the back button out of the garage leaves the app
 * rather than landing the reader back here to watch it again.
 */

/**
 * When each stage starts, in milliseconds from mount, and how long the fill
 * runs. Read as a schedule rather than as six numbers: the streaks lead by a
 * beat, the wordmark rides in on them, the badge lands a third of a second
 * later, and the words arrive only once the lockup has stopped moving.
 *
 * The whole thing is 2.4 seconds. Long enough to be a moment; short enough that
 * the "hand over on tap" escape is a courtesy rather than a necessity.
 */
const STAGE_MS = {
    streaks: 80,
    wordmark: 200,
    badge: 560,
    proposition: 900,
    fill: 1080,
};

/** How long the track takes to fill, which is also what the reader waits for. */
const FILL_MS = 1320;

const HANDOVER_AFTER_MS = STAGE_MS.fill + FILL_MS;

/**
 * Five streaks, because that is what §03 draws and because an odd number reads
 * as motion where an even one reads as a pattern. The index is what staggers
 * them; the sheet turns it into a delay, a length and a vertical offset.
 */
const STREAK_COUNT = 5;

/**
 * Does this reader want the animation at all?
 *
 * `matchMedia` as a defaulted parameter, per the repo's rule for browser APIs —
 * and here it also means a test can assert BOTH branches of the one screen in
 * the product that is entirely animation. A browser without `matchMedia` (an old
 * webview) answers false and gets the sequence, which is the safe way round: a
 * reader who asked for less motion is always detectable, and one who did not ask
 * for anything has expressed no preference.
 */
export function prefersReducedMotion(matchMedia = typeof window === 'undefined' ? null : window.matchMedia) {
    try {
        return Boolean(matchMedia?.call(window, '(prefers-reduced-motion: reduce)')?.matches);
    } catch (error) {
        return false;
    }
}

export default function SplashScreen({
    storage = sessionMemory(),
    matchMedia = typeof window === 'undefined' ? null : window.matchMedia,
}) {
    const navigate = useNavigate();
    const { localeCode } = useLocale();
    const t = useTranslate();

    // Both read ONCE, in a lazy initialiser, and never again. Re-reading either
    // on a later render would let the screen change what it is halfway through:
    // the session flag is written by this very component, so a re-read after the
    // handover would flip a playing splash into a still one mid-animation.
    const [isStill] = useState(() => (
        hasSeen(SPLASH_HANDED_OVER, storage) || prefersReducedMotion(matchMedia)
    ));

    // The handover is idempotent because three things can trigger it — the
    // timer, a tap, a key — and they routinely fire together: releasing a tap
    // that lands as the fill completes would otherwise navigate twice, which
    // pushes a history entry the `replace` was there to prevent.
    const handedOver = useRef(false);

    const handOver = useCallback(() => {
        if (handedOver.current) {
            return;
        }

        handedOver.current = true;
        rememberSeen(SPLASH_HANDED_OVER, storage);
        navigate(`/${localeCode}/garage`, { replace: true });
    }, [localeCode, navigate, storage]);

    useEffect(() => {
        if (isStill) {
            // No timer and no wait: a reader who has seen this, or who has asked
            // for less motion, gets the garage. The still lockup below is what
            // covers the one frame between mounting and the redirect landing.
            handOver();

            return undefined;
        }

        const timer = window.setTimeout(handOver, HANDOVER_AFTER_MS);

        return () => window.clearTimeout(timer);
    }, [handOver, isStill]);

    // On WINDOW rather than on the section, so a tap anywhere counts — including
    // the safe-area gutters, which are part of the screen the reader sees and
    // not part of the element that draws it. `keydown` for the same reason a
    // visible button exists below: the gesture must never be the only way
    // through (NFR-04).
    useEffect(() => {
        if (isStill) {
            return undefined;
        }

        window.addEventListener('pointerdown', handOver);
        window.addEventListener('keydown', handOver);

        return () => {
            window.removeEventListener('pointerdown', handOver);
            window.removeEventListener('keydown', handOver);
        };
    }, [handOver, isStill]);

    return (
        <section
            className={`splash${isStill ? ' splash--still' : ' splash--playing'}`}
            style={{
                '--stage-streaks': `${STAGE_MS.streaks}ms`,
                '--stage-wordmark': `${STAGE_MS.wordmark}ms`,
                '--stage-badge': `${STAGE_MS.badge}ms`,
                '--stage-proposition': `${STAGE_MS.proposition}ms`,
                '--stage-fill': `${STAGE_MS.fill}ms`,
                '--fill-run': `${FILL_MS}ms`,
            }}
        >
            {/* Pure decoration and pure motion — there is nothing here to
                announce, and a reader who cannot see them is not missing a fact. */}
            <div className="splash__streaks" aria-hidden="true">
                {Array.from({ length: STREAK_COUNT }, (unused, index) => (
                    <span className="splash__streak" key={index} style={{ '--streak': index }} />
                ))}
            </div>

            <div className="splash__lockup">
                {/* Behind the wordmark, and drawn first so it is behind it in
                    paint order too — the badge is the ground the type stands on,
                    which is why it arrives after and lands underneath. */}
                <BrandBadge
                    className="splash__badge"
                    /* Each step mirrors `.splash__badge` in
                       `splashHandover.css`, which holds this screen's desktop
                       composition. The badge is drawn at §03's ratio to the
                       wordmark, so the two ladders move together. */
                    sizes="(min-width: 1920px) 448px, (min-width: 1440px) 344px, (min-width: 1024px) 264px, 120px"
                    alt=""
                />
                {/* The document's `h1`. This screen has one thing on it and this
                    is it, so the product's name is the heading rather than a
                    picture beside one. */}
                <h1 className="splash__heading">
                    <BrandWordmark
                        className="splash__wordmark"
                        /* The lockup's ladder, restated for the preload parser.
                           ------------------------------------------------------
                           `BrandMark.js` cuts the wordmark at 480, 960 and 1440
                           — and until this line changed, the 360px cap meant a
                           2560px display asked for the 480 and the other two
                           files had never been requested at 1x in the product's
                           life. `sizes` is what fixes that, not the CSS: it is
                           read before the stylesheet resolves, so a wordmark
                           grown to 1000px behind a `sizes` still saying 360px
                           would fetch the smallest rung and upscale it.

                           Each step is the widest the picture gets in its band,
                           mirroring `.splash__wordmark` in `splashHandover.css`,
                           which is where this screen's desktop composition
                           lives. Change either and this line is part of the
                           change. */
                        sizes="(min-width: 1920px) 1120px, (min-width: 1440px) 860px, (min-width: 1024px) 660px, (min-width: 480px) 360px, 72vw"
                        alt={t('brand.name')}
                    />
                </h1>
            </div>

            <p className="splash__proposition">{t('brand.proposition')}</p>
            <p className="splash__tagline">{t('splash.tagline')}</p>

            {/* Indeterminate on purpose: it reports a WAIT, not a measurement.
                There is nothing being downloaded whose progress this could be —
                giving it a percentage would be the interface inventing a number. */}
            <div
                className="splash__track"
                role="progressbar"
                aria-label={t('a11y.splashProgress')}
                aria-valuetext={t('splash.loading')}
            >
                <span className="splash__fill" />
            </div>

            {/* The window listeners above cover a tap; this covers everything
                else. It is the only focusable thing on the screen, so a reader
                arriving by keyboard or switch lands straight on the way out. */}
            <button type="button" className="splash__enter" onClick={handOver}>
                {t('splash.enter')}
            </button>
        </section>
    );
}
